import { EngagementScoreTerminationReason, StreamTimelineMetadata } from '@quickplay/player-interface';
import { createBrowserVisibilityTracker } from './BrowserVisibilityTracker';
import { createLiveChannelEngagementScoreTracker } from './LiveChannelEngagementScoreTracker';
import { createLiveProgramEngagementScoreTracker } from './LiveProgramEngagementScoreTracker';
import { createVodEngagementScoreTracker } from './VodEngagementScoreTracker';
import { EngagementObservation, MuteState } from './EngagementBehaviorTracker';
import { TimingSnapshot } from './EngagementScoreUtils';

interface EngagementScoreModuleOptions {
  videoElement?: HTMLVideoElement;
  isLive: () => boolean;
  getTimingSnapshot: () => TimingSnapshot | undefined;
  getEligibleDuration: () => unknown;
  getPlaybackObservation: () => {
    isSeeking: boolean;
    isBuffering: boolean;
    muteState: MuteState;
  };
  onScore: (score: string) => void;
}

export class EngagementScoreModule {
  private readonly isLive: () => boolean;
  private readonly visibilityTracker?: ReturnType<typeof createBrowserVisibilityTracker>;
  private readonly vodTracker?: ReturnType<typeof createVodEngagementScoreTracker>;
  private readonly liveProgramTracker?: ReturnType<typeof createLiveProgramEngagementScoreTracker>;
  private readonly liveChannelTracker?: ReturnType<typeof createLiveChannelEngagementScoreTracker>;

  constructor(options: EngagementScoreModuleOptions) {
    this.isLive = options.isLive;
    this.visibilityTracker = createBrowserVisibilityTracker(options.videoElement);
    const getObservation = (): EngagementObservation => ({
      ...options.getPlaybackObservation(),
      visibility: this.visibilityTracker!.snapshot(),
    });
    const onScore = (score: string) => {
      options.onScore(score);
    };

    this.liveChannelTracker = createLiveChannelEngagementScoreTracker({ onScore });
    this.vodTracker = createVodEngagementScoreTracker({
      getTimingSnapshot: options.getTimingSnapshot,
      getEligibleDuration: options.getEligibleDuration,
      getObservation,
      onScore,
    });
    this.liveProgramTracker = createLiveProgramEngagementScoreTracker({
      getTimingSnapshot: options.getTimingSnapshot,
      getObservation,
      onSegmentFinalized: segment => this.liveChannelTracker!.includeSegment(segment),
      onScore,
    });
  }

  start(): void {
    if (!this.isLive()) {
      this.vodTracker?.start();
    }
  }
  observe(): void {
    this.vodTracker?.observe();
    this.liveProgramTracker?.observe();
  }
  programBoundary(metadata?: StreamTimelineMetadata): void {
    const live = this.isLive();
    console.info(`[Engagement Score] program boundary received live=${live}`, metadata);
    if (!live) {
      console.info('[Engagement Score] program boundary ignored because playback is not live');
      return;
    }

    this.liveProgramTracker?.onProgramBoundary(metadata);
  }
  finalize(reason: EngagementScoreTerminationReason): void {
    console.info(`[Engagement Score] module finalize reason=${reason} live=${this.isLive()}`);
    this.vodTracker?.finalize(reason);
    this.liveProgramTracker?.finalize(reason);
    this.liveChannelTracker?.finalize(reason);
  }
  destroy(): void {
    this.visibilityTracker?.destroy();
  }
}
